import { useState, useCallback } from 'react';
import { useStoreInfo } from '@/hooks/useStoreInfo';
import { setVendedor } from '@/services/vendedor';
import type { Vendedor } from '@/services/store.service';

function abrirWhatsApp(numero: string, mensaje: string) {
  window.open(`https://wa.me/${numero}?text=${mensaje}`, '_blank', 'noopener,noreferrer');
}

/**
 * Envía el pedido por WhatsApp al vendedor que corresponde:
 * - si el cliente entró por el link de un vendedor (o ya eligió uno), a ese;
 * - si no, primero le muestra el selector de vendedores;
 * - sin vendedores configurados, al número general de la tienda.
 */
export function usePedidoWhatsApp() {
  const { info, vendedor, origenVendedor, vendedores } = useStoreInfo();
  const [abierto, setAbierto] = useState(false);
  // Mensaje ya codificado que espera a que el cliente elija vendedor
  const [pendiente, setPendiente] = useState<string | null>(null);

  const enviar = useCallback(
    (mensaje: string) => {
      if (vendedor || vendedores.length === 0) {
        abrirWhatsApp(info.whatsapp, mensaje);
      } else {
        setPendiente(mensaje);
        setAbierto(true);
      }
    },
    [vendedor, vendedores.length, info.whatsapp],
  );

  const elegir = useCallback(
    (v: Vendedor | null) => {
      if (v) setVendedor(v.codigo, 'elegido');
      // "Sin preferencia" va al número general y no guarda nada
      if (pendiente) abrirWhatsApp(v ? v.whatsapp : info.phone, pendiente);
      setPendiente(null);
      setAbierto(false);
    },
    [pendiente, info.phone],
  );

  const cerrar = useCallback(() => {
    setPendiente(null);
    setAbierto(false);
  }, []);

  return {
    enviar,
    vendedor,
    // Quien llegó por el link de un vendedor se queda con él
    puedeCambiar: vendedores.length > 1 && origenVendedor !== 'link',
    cambiarVendedor: () => setAbierto(true),
    selector: { abierto, vendedores, actual: vendedor, onElegir: elegir, onCerrar: cerrar },
  };
}
